/**
 * ragoneAxes — shared Basis/Domain axis metadata for Ragone-style scatter
 * plots. Mirrors RagonePlotECharts.getAxisValues (the Explorer Ragone) so the
 * BATEMO ION chat plot follows the same global Basis/Domain semantics:
 *   x = power / current, y = energy / capacity.
 *
 * The chat plot's coordinates are embedded per-point by the backend
 * (rank_cells_for_application → plot_data.points[*].coords[domain][basis]);
 * this module supplies the matching axis labels/units plus a resolver with a
 * flat-x/y fallback for older artifacts (or unresolved variants) that lack
 * coords. Keep the label/unit table in sync with RagonePlotECharts.
 */

export const RAGONE_AXIS_META = {
  'energy-power': {
    abs: { xLabel: 'Power', yLabel: 'Energy', xUnit: 'W', yUnit: 'Wh' },
    grav: {
      xLabel: 'Specific Power',
      yLabel: 'Specific Energy',
      xUnit: 'W/kg',
      yUnit: 'Wh/kg',
    },
    vol: {
      xLabel: 'Power Density',
      yLabel: 'Energy Density',
      xUnit: 'W/L',
      yUnit: 'Wh/L',
    },
  },
  'capacity-current': {
    abs: { xLabel: 'Current', yLabel: 'Capacity', xUnit: 'A', yUnit: 'Ah' },
    grav: {
      xLabel: 'Specific Current',
      yLabel: 'Specific Capacity',
      xUnit: 'A/kg',
      yUnit: 'Ah/kg',
    },
    vol: {
      xLabel: 'Current Density',
      yLabel: 'Capacity Density',
      xUnit: 'A/L',
      yUnit: 'Ah/L',
    },
  },
}

const DEFAULT_DOMAIN = 'energy-power'
const DEFAULT_BASIS = 'grav'

/** Axis labels + units for a (basis, domain) pair, with sensible fallbacks. */
export function ragoneAxisMeta(basis, domain) {
  const d = RAGONE_AXIS_META[domain] || RAGONE_AXIS_META[DEFAULT_DOMAIN]
  return d[basis] || d[DEFAULT_BASIS]
}

/**
 * Resolve a plot point's {x, y} for the given basis/domain from its embedded
 * `coords`. Falls back to the point's flat x/y when coords (or the requested
 * variant) is absent — so older artifacts and unresolved variants still plot.
 */
export function resolveXY(point, basis, domain) {
  const c =
    point && point.coords && point.coords[domain] && point.coords[domain][basis]
  if (c && Number.isFinite(c.x) && Number.isFinite(c.y)) {
    return { x: c.x, y: c.y }
  }
  return { x: point ? point.x : undefined, y: point ? point.y : undefined }
}

/**
 * Resolve a threshold LINE's scalar position for the given basis/domain from its
 * embedded `coords` (a number per (domain, basis), unlike a point's {x,y}).
 * Returns null when the threshold doesn't map to the current toggle — the line
 * is then hidden (a per-cell energy requirement, say, only has meaning in the
 * basis it was derived in). Mirrors resolveXY's coords lookup.
 */
export function resolveThreshold(threshold, basis, domain) {
  const c =
    threshold &&
    threshold.coords &&
    threshold.coords[domain] &&
    threshold.coords[domain][basis]
  return Number.isFinite(c) ? c : null
}
